import { inject, Injectable } from '@angular/core';
import { RegistrationType } from '../enums/registration-type.enum';
import { Contact } from '../models/contact.model';
import { Registration } from '../models/registration.model';
import { ContactService } from './contact.service';
import { RegistrationService } from './registration.service';
import { SyncStateType } from '@shared/enums/sync-state-type.enum';

@Injectable({
  providedIn: 'root',
})
export class CustomerNumberService {
  private readonly _registrationService = inject(RegistrationService);
  private readonly _contactService = inject(ContactService);

  /**
   * Determines the next customer number by reading all contacts from the local
   * IndexedDB, finding the highest existing numeric CustomerReference value, and
   * returning MAX + 1.  Non-numeric values are ignored.
   * Returns 1 if no numeric customer number exists yet.
   */
  public async getNextCustomerNumber(): Promise<string> {
    const result = await this._contactService.getAll(this._contactService._limit);

    let max = 0;

    for (const contact of result.data) {
      const reg = contact.registrations?.find(
        (r) => r.registrationType === RegistrationType.CustomerReference,
      );
      if (reg?.registrationNumber) {
        const parsed = parseInt(reg.registrationNumber.trim(), 10);
        if (!isNaN(parsed) && parsed > max) {
          max = parsed;
        }
      }
    }

    return String(max + 1);
  }

  /**
   * Builds a CustomerReference registration with a generated number and embeds
   * it directly into the contact's registrations array so it is persisted
   * atomically as part of CreateContactV2 — no separate CreateRegistration call
   * needed.  Call this BEFORE ContactService.create().
   */
  public async embedCustomerNumberRegistration(contact: Contact): Promise<void> {
    const alreadyHasCustomerNumber = contact.registrations?.some(
      (r) => r.registrationType === RegistrationType.CustomerReference,
    );

    if (alreadyHasCustomerNumber) {
      return;
    }

    const registrationNumber = await this.getNextCustomerNumber();

    const registration: Registration = {
      contactUId: contact.uId,
      registrationType: RegistrationType.CustomerReference,
      registrationNumber,
      uId: '00000000-0000-0000-0000-000000000000',
      tenantUId: '00000000-0000-0000-0000-000000000000',
      userUId: '00000000-0000-0000-0000-000000000000',
      concurrencyIdentificator: crypto.randomUUID(),
      concurrencyStamp: new Date().toISOString(),
      stampCreate: new Date().toISOString(),
      syncType: SyncStateType.INSERT,
    };

    if (contact.registrations) {
      contact.registrations.push(registration);
    } else {
      contact.registrations = [registration];
    }
  }

  /**
   * Creates a CustomerReference registration with a generated number via a
   * separate CreateRegistration call (used when the contact already exists).
   */
  public async generateCustomerNumber(customer: Contact): Promise<void> {
    const alreadyHasCustomerNumber = customer.registrations?.some(
      (r) => r.registrationType === RegistrationType.CustomerReference,
    );

    if (alreadyHasCustomerNumber) {
      return;
    }

    const registrationNumber = await this.getNextCustomerNumber();

    const registration: Registration = {
      contactUId: customer.uId,
      registrationType: RegistrationType.CustomerReference,
      registrationNumber,
      uId: '00000000-0000-0000-0000-000000000000',
      tenantUId: '00000000-0000-0000-0000-000000000000',
      userUId: '00000000-0000-0000-0000-000000000000',
      concurrencyIdentificator: crypto.randomUUID(),
      concurrencyStamp: new Date().toISOString(),
      stampCreate: new Date().toISOString(),
      syncType: SyncStateType.INSERT,
    };

    const result = await this._registrationService.create(registration);

    if (result) {
      if (customer.registrations) {
        customer.registrations.push(result);
      } else {
        customer.registrations = [result];
      }
    }
  }
}
